import React, { useEffect, useState } from "react";
import { DELIVERY_ENABLED, isViewEnabled } from "../features";
import "../StockInward.css";
import { createUser, getUsers, updateUser } from "../storage";

const EXPENSE_PAGE_IDS = ["expenseDelivery", "expenseStall", "expenseMarket", "expenseMaster"];

const PAGE_OPTIONS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "inward", label: "Material Purchase" },
  { id: "purchaseReports", label: "Material Purchase Report" },
  { id: "billing", label: "Sales" },
  { id: "billingReport", label: "Sales Report" },
  { id: "expenseMaster", label: "Master Expense" },
  { id: "itemMaster", label: "Item Master" },
  { id: "supplierMaster", label: "Supplier Master" },
  { id: "customerMaster", label: "Customer Master" },
  { id: "expenseNameMaster", label: "Expense Master" },
  { id: "employeeMaster", label: "Employee Master" },
  { id: "employeeAttendance", label: "Employee Attendance" },
].filter((page) => isViewEnabled(page.id));

function normalizeAllowedViews(allowed) {
  const set = new Set(Array.isArray(allowed) ? allowed.filter(Boolean) : []);
  if (set.has("miscExpenses")) {
    EXPENSE_PAGE_IDS.forEach((id) => set.add(id));
    set.delete("miscExpenses");
  }
  return Array.from(set);
}

function hasPageAccess(allowed, viewId) {
  return normalizeAllowedViews(allowed).includes(viewId);
}

function toggleAllowed(allowed, viewId) {
  const list = normalizeAllowedViews(allowed);
  if (list.includes(viewId)) {
    return list.filter((v) => v !== viewId);
  }
  return [...list, viewId];
}

export default function UserAccess({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [passwordEdits, setPasswordEdits] = useState({});
  const [error, setError] = useState("");

  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    role: "executive",
    allowedViews: [DELIVERY_ENABLED ? "billing" : "inward"],
  });
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setError("");
    setLoading(true);
    try {
      const list = await getUsers();
      setUsers(
        (list || []).map((user) => ({
          ...user,
          allowedViews: normalizeAllowedViews(user?.allowedViews),
        }))
      );
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");

    const username = newUser.username.trim();
    const password = newUser.password;
    const role = newUser.role;
    const allowedViews = role === "admin" ? [] : normalizeAllowedViews(newUser.allowedViews);

    if (!username || !password) {
      setError("Username and password are required.");
      return;
    }

    setCreating(true);
    try {
      const created = await createUser({ username, password, role, allowedViews });
      setUsers((prev) => {
        const next = [
          ...prev,
          { ...created, allowedViews: normalizeAllowedViews(created?.allowedViews) },
        ].filter(Boolean);
        next.sort((a, b) => String(a.username || "").localeCompare(String(b.username || "")));
        return next;
      });
      setNewUser({ username: "", password: "", role: "executive", allowedViews: [DELIVERY_ENABLED ? "billing" : "inward"] });
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to create user.");
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (user) => {
    setError("");
    setSaving((prev) => ({ ...prev, [user.id]: true }));
    try {
      const password = String(passwordEdits[user.id] || "");
      const payload = {
        role: user.role,
        allowedViews: user.role === "admin" ? [] : normalizeAllowedViews(user.allowedViews),
        active: user.active ?? true,
      };
      if (password.trim()) {
        payload.password = password;
      }
      const updated = await updateUser(user.id, payload);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === updated.id
            ? { ...u, ...updated, allowedViews: normalizeAllowedViews(updated?.allowedViews) }
            : u
        )
      );
      setPasswordEdits((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to save changes.");
    } finally {
      setSaving((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
    }
  };

  if (currentUser?.role !== "admin") {
    return (
      <div className="stock-inward-container" style={{ paddingTop: 0 }}>
        <div className="form-card">
          <div className="card-header">
            <h2 className="card-title">User Rights</h2>
          </div>
          <div className="inward-form">
            <div className="empty-state" style={{ padding: "16px" }}>
              You do not have access to this page.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stock-inward-container" style={{ paddingTop: 0 }}>
      <div className="form-card">
        <div className="card-header">
          <h2 className="card-title">User Rights</h2>
        </div>
        <form className="inward-form" onSubmit={handleCreate}>
          {error ? <div className="login-error">{error}</div> : null}
          <div className="form-grid">
            <div className="field fl-field">
              <input
                id="new-username"
                className="fl-input input-field"
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser((p) => ({ ...p, username: e.target.value }))}
                placeholder=" "
                required
              />
              <label htmlFor="new-username" className="fl-label">
                Username
              </label>
            </div>
            <div className="field fl-field">
              <input
                id="new-password"
                className="fl-input input-field"
                type="text"
                value={newUser.password}
                onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
                placeholder=" "
                required
              />
              <label htmlFor="new-password" className="fl-label">
                Password
              </label>
            </div>
            <div className="field fl-field">
              <select
                id="new-role"
                className="fl-input input-field"
                value={newUser.role}
                onChange={(e) => {
                  const role = e.target.value;
                  setNewUser((p) => ({
                    ...p,
                    role,
                    allowedViews: role === "admin" ? [] : p.allowedViews,
                  }));
                }}
                style={{ paddingTop: "14px" }}
              >
                <option value="admin">Admin</option>
                <option value="executive">Executive</option>
              </select>
              <label htmlFor="new-role" className="fl-label">
                Role
              </label>
            </div>
            <div className="field full" style={{ marginTop: 4 }}>
              <label style={{ fontSize: 12, color: "#6b7280" }}>Page Access</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6 }}>
                {PAGE_OPTIONS.map((opt) => (
                  <label key={opt.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={newUser.role === "admin" ? true : hasPageAccess(newUser.allowedViews, opt.id)}
                      disabled={newUser.role === "admin"}
                      onChange={() =>
                        setNewUser((p) => ({
                          ...p,
                          allowedViews: toggleAllowed(p.allowedViews, opt.id),
                        }))
                      }
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={creating}>
              {creating ? "Creating..." : "Create User"}
            </button>
            <button type="button" className="btn-secondary" onClick={refresh} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </form>
      </div>

      <div className="entries-card">
        <div className="card-header" style={{ borderBottom: "none" }}>
          <h2 className="card-title">Users</h2>
          <span className="entry-count">{users.length} users</span>
        </div>
        <div className="entries-table-container">
          <table className="entries-table">
            <thead>
              <tr>
                <th>Username</th>
                <th style={{ width: 140 }}>Role</th>
                <th style={{ width: 140 }}>Status</th>
                <th style={{ width: 200 }}>Password</th>
                <th>Page Access</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const isSelf = currentUser?.id === u.id;
                  const isAdmin = u.role === "admin";
                  const isActive = u.active ?? true;
                  return (
                    <tr key={u.id} style={!isActive ? { opacity: 0.6 } : undefined}>
                      <td>{u.username}</td>
                      <td>
                        <select
                          className="input-field compact"
                          value={u.role || "executive"}
                          disabled={isSelf || !!saving[u.id]}
                          onChange={(e) =>
                            setUsers((prev) =>
                              prev.map((x) =>
                                x.id !== u.id
                                  ? x
                                  : {
                                      ...x,
                                      role: e.target.value,
                                      allowedViews: e.target.value === "admin" ? [] : normalizeAllowedViews(x.allowedViews),
                                    }
                              )
                            )
                          }
                        >
                          <option value="admin">Admin</option>
                          <option value="executive">Executive</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className="input-field compact"
                          value={isActive ? "active" : "inactive"}
                          disabled={isSelf || !!saving[u.id]}
                          onChange={(e) =>
                            setUsers((prev) =>
                              prev.map((x) =>
                                x.id !== u.id
                                  ? x
                                  : { ...x, active: e.target.value === "active" }
                              )
                            )
                          }
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </td>
                      <td>
                        <input
                          className="input-field compact"
                          type="text"
                          value={passwordEdits[u.id] || ""}
                          disabled={!!saving[u.id]}
                          placeholder="New password"
                          onChange={(e) =>
                            setPasswordEdits((prev) => ({ ...prev, [u.id]: e.target.value }))
                          }
                        />
                      </td>
                      <td>
                        {isAdmin ? (
                          <span style={{ color: "#16a34a", fontWeight: 600 }}>All pages</span>
                        ) : (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                            {PAGE_OPTIONS.map((opt) => (
                              <label key={opt.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <input
                                  type="checkbox"
                                  checked={hasPageAccess(u.allowedViews, opt.id)}
                                  disabled={!!saving[u.id]}
                                  onChange={() =>
                                    setUsers((prev) =>
                                      prev.map((x) =>
                                        x.id !== u.id
                                          ? x
                                          : { ...x, allowedViews: toggleAllowed(x.allowedViews, opt.id) }
                                      )
                                    )
                                  }
                                />
                                <span>{opt.label}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="actions">
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => handleSave(u)}
                          disabled={!!saving[u.id]}
                          style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                        >
                          {saving[u.id] ? "Saving..." : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
